import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ClipboardCheck, Edit, Trash2, AlertCircle, X } from 'lucide-react';
import api from '../../api/apiClient';
import PageHeader from '../../components/PageHeader';

interface ComplianceCheck {
  id: string;
  propertyId: number;
  propertyAddress: string;
  checkType: string;
  status: string;
  expiryDate?: string;
  completedDate?: string;
  certificateNumber?: string;
  certificateUrl?: string;
  inspectorName?: string;
  inspectorLicense?: string;
  notes?: string;
  reminderDaysBefore: number;
  daysUntilExpiry?: number;
  isExpired: boolean;
  needsAttention: boolean;
}

const CHECK_TYPES = [
  { value: 'SMOKE_ALARM', label: 'Smoke Alarm Check' },
  { value: 'GAS_SAFETY', label: 'Gas Safety Check' },
  { value: 'ELECTRICAL', label: 'Electrical Safety Check' },
  { value: 'POOL_FENCE', label: 'Pool Fence Compliance' },
  { value: 'FIRE_EXTINGUISHER', label: 'Fire Extinguisher' },
  { value: 'PEST_CONTROL', label: 'Pest Control' },
  { value: 'OTHER', label: 'Other' },
];

export default function ComplianceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [check, setCheck] = useState<ComplianceCheck | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    checkType: '',
    expiryDate: '',
    completedDate: '',
    certificateNumber: '',
    inspectorName: '',
    inspectorLicense: '',
    notes: '',
    reminderDaysBefore: '30',
  });

  useEffect(() => {
    if (id) {
      loadCheck();
    }
  }, [id]);

  const loadCheck = async () => {
    setLoading(true);
    try {
      const response = await api.get<ComplianceCheck>(`/compliance/checks/${id}`);
      setCheck(response);
      setForm({
        checkType: response.checkType || '',
        expiryDate: response.expiryDate || '',
        completedDate: response.completedDate || '',
        certificateNumber: response.certificateNumber || '',
        inspectorName: response.inspectorName || '',
        inspectorLicense: response.inspectorLicense || '',
        notes: response.notes || '',
        reminderDaysBefore: response.reminderDaysBefore?.toString() || '30',
      });
    } catch (err) {
      console.error('Failed to load compliance check:', err);
      setError('Failed to load compliance check');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!check) return;

    setSaving(true);
    setError('');
    try {
      await api.put(`/compliance/checks/${id}`, {
        propertyId: check.propertyId,
        checkType: form.checkType,
        expiryDate: form.expiryDate || null,
        completedDate: form.completedDate || null,
        certificateNumber: form.certificateNumber || null,
        inspectorName: form.inspectorName || null,
        inspectorLicense: form.inspectorLicense || null,
        notes: form.notes || null,
        reminderDaysBefore: parseInt(form.reminderDaysBefore) || 30,
      });
      await loadCheck();
      setEditing(false);
    } catch (err: any) {
      setError(err.message || 'Failed to update compliance check');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/compliance/checks/${id}`);
      navigate('/owner/compliance');
    } catch (err: any) {
      setError(err.message || 'Failed to delete compliance check');
      setShowDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };

  const getStatusColor = () => {
    if (!check) return 'bg-gray-100 text-gray-800';
    if (check.isExpired) return 'bg-red-100 text-red-800';
    if (check.needsAttention) return 'bg-yellow-100 text-yellow-800';
    if (check.status === 'COMPLETED') return 'bg-green-100 text-green-800';
    return 'bg-blue-100 text-blue-800';
  };

  const getStatusLabel = () => {
    if (!check) return 'Unknown';
    if (check.isExpired) return 'EXPIRED';
    if (check.needsAttention) return 'EXPIRING SOON';
    return check.status;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const getCheckTypeLabel = (type: string) => {
    return CHECK_TYPES.find(t => t.value === type)?.label || type;
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!check) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8 text-center">
        <p className="text-gray-600">Compliance check not found</p>
        <button onClick={() => navigate('/owner/compliance')} className="mt-4 text-secondary hover:text-primary">
          Back to Compliance
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Header */}
      <PageHeader
        title={getCheckTypeLabel(check.checkType)}
        subtitle={check.propertyAddress}
        icon={<ClipboardCheck className="w-8 h-8" />}
        backPath="/owner/compliance"
      >
        <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor()}`}>
          {getStatusLabel()}
        </span>
        {!editing && (
          <>
            <button
              onClick={() => setEditing(true)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              <Edit className="w-4 h-4" />
              Edit
            </button>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </>
        )}
      </PageHeader>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800">{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Expiry Warning */}
      {check.needsAttention && !check.isExpired && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600" />
          <span className="text-yellow-800">
            This check expires in {check.daysUntilExpiry} days ({formatDate(check.expiryDate)})
          </span>
        </div>
      )}

      {check.isExpired && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800">
            This check has expired! Please schedule a new inspection.
          </span>
        </div>
      )}

      {/* Details */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-4">
          <ClipboardCheck className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold text-gray-900">Check Details</h2>
        </div>

        {editing ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Check Type *</label>
              <select
                value={form.checkType}
                onChange={(e) => setForm({ ...form, checkType: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
              >
                {CHECK_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Completed Date</label>
                <input
                  type="date"
                  value={form.completedDate}
                  onChange={(e) => setForm({ ...form, completedDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Date</label>
                <input
                  type="date"
                  value={form.expiryDate}
                  onChange={(e) => setForm({ ...form, expiryDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Certificate Number</label>
              <input
                type="text"
                value={form.certificateNumber}
                onChange={(e) => setForm({ ...form, certificateNumber: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Inspector Name</label>
                <input
                  type="text"
                  value={form.inspectorName}
                  onChange={(e) => setForm({ ...form, inspectorName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Inspector License</label>
                <input
                  type="text"
                  value={form.inspectorLicense}
                  onChange={(e) => setForm({ ...form, inspectorLicense: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent resize-none"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => setEditing(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Check Type</p>
              <p className="font-medium text-gray-900">{getCheckTypeLabel(check.checkType)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getStatusColor()}`}>
                {getStatusLabel()}
              </span>
            </div>
            <div>
              <p className="text-sm text-gray-500">Completed Date</p>
              <p className="font-medium text-gray-900">{formatDate(check.completedDate)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Expiry Date</p>
              <p className="font-medium text-gray-900">{formatDate(check.expiryDate)}</p>
            </div>
            {check.certificateNumber && (
              <div>
                <p className="text-sm text-gray-500">Certificate Number</p>
                <p className="font-medium text-gray-900">{check.certificateNumber}</p>
              </div>
            )}
            {check.inspectorName && (
              <div>
                <p className="text-sm text-gray-500">Inspector</p>
                <p className="font-medium text-gray-900">{check.inspectorName}</p>
              </div>
            )}
            {check.inspectorLicense && (
              <div>
                <p className="text-sm text-gray-500">Inspector License</p>
                <p className="font-medium text-gray-900">{check.inspectorLicense}</p>
              </div>
            )}
            {check.notes && (
              <div className="col-span-2">
                <p className="text-sm text-gray-500">Notes</p>
                <p className="font-medium text-gray-900">{check.notes}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Delete Compliance Check</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this compliance check? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



